<template>
  <component
    :is="interactive ? WdsButtonGhost : 'div'"
    class="supplier-name-column"
    :data-test="`supplier-name-${rowIndex}`"
    @click="openDetails"
  >
    <WdsAvatarLabel :path="avatarPath" :initials="supplier.name" truncated>
      <template #subText>
        <WdsStack direction-column>
          <WdsStack direction-row align-center gap-xs>
            <WdsText primary>{{ supplier.name }}</WdsText>
            <WdsTag v-if="isInvited" sm warning :data-test="`supplier-invited-${rowIndex}`">
              {{ $t('Invited') }}
            </WdsTag>
          </WdsStack>
          <WdsStack v-if="market" direction-row align-center gap-xs>
            <WdsIcon :size="14" :name="`flag-${market.code.toLowerCase()}` as WdsIconNameEnum" />
            <WdsText secondary small>{{ market.name }}</WdsText>
          </WdsStack>
        </WdsStack>
      </template>
    </WdsAvatarLabel>
  </component>
</template>

<script lang="ts" setup>
import { computed, inject } from 'vue'
import {
  WdsAvatarLabel,
  WdsButtonGhost,
  WdsIcon,
  WdsIconNameEnum,
  WdsStack,
  WdsTag,
  WdsText,
} from '@worksome/wds-components'
import { FragmentType, graphql, useFragment } from '@/gql'
import { ContactStatus } from '@/gql/graphql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { useAvatarPath } from '@/composables/useAvatarPath'
import { TableInModal } from '@/components/GraphqlDataTable/injection'
import { useTablePanels } from '@/components/GraphqlDataTable/useTablePanels'

const { openRecruiterDetailPanel } = useTablePanels()

const SupplierNameColumn = graphql(`
  fragment SupplierNameColumn on SupplierClient {
    id
    supplier {
      id
      name
      avatar
    }
    companyRecruiter {
      id
      email
      status
      recruiter {
        id
        market {
          code
          name
        }
      }
    }
  }
`)

const props = defineProps<{
  row: FragmentType<typeof SupplierNameColumn>
  rowIndex?: number
}>()

const column = computed(() => useFragment(SupplierNameColumn, props.row))
const rowIndex = computed(() => props.rowIndex ?? 0)

// Name and avatar come off the supplier account, so a company supplier renders like an agency.
const supplier = computed(() => column.value.supplier)
const relation = computed(() => column.value.companyRecruiter ?? null)
const market = computed(() => relation.value?.recruiter.market ?? null)
const isInvited = computed(
  () => relation.value !== null && relation.value.status !== ContactStatus.Active,
)

const avatarPath = useAvatarPath(
  () => supplier.value.avatar,
  () => supplier.value.id,
)

const inModal = inject(TableInModal, false)

// Only agency relations have a panel, and never inside a modal; otherwise the name is plain text.
const interactive = computed(() => !inModal && relation.value !== null)

const openDetails = () => {
  const companyRecruiterId = relation.value?.id

  if (!interactive.value || !companyRecruiterId) return

  openRecruiterDetailPanel({ companyRecruiterId })
}
</script>

<style lang="scss">
.supplier-name-column {
  text-align: left;
}
</style>
